'use client';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { CandlestickChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  MarkPointComponent,
  VisualMapComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { StockHistory, StockCandle } from '../../shared';

// Register ECharts components
echarts.use([
  CandlestickChart,
  GridComponent,
  TooltipComponent,
  MarkPointComponent,
  VisualMapComponent,
  CanvasRenderer,
]);

interface S1SignalChartProps {
  history: StockHistory;
  showSignals?: boolean;
  showMA?: boolean;
}

// 信号类型定义
type SignalType = 'B' | 'S' | 'E' | null;

interface ChartDataPoint {
  date: string;
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  // 蜡烛图数据
  candleBody: [number, number]; // [min, max] of open/close
  wickHigh: number;
  wickLow: number;
  isUp: boolean;
  // 信号数据
  signal: SignalType;
  signalY: number; // 信号标记的Y坐标
  trendCount: number; // 连续趋势天数
  volumeRatio: number; // 成交量比率
  isExceptional: boolean; // 是否异常成交量
}

// 计算移动平均线（返回与 candles 等长数组，不足期用 null）
function calculateMA(candles: StockCandle[], period: number): Array<number | null> {
  return candles.map((_, index) => {
    if (index + 1 < period) return null;
    const slice = candles.slice(index + 1 - period, index + 1);
    const sum = slice.reduce((acc, c) => acc + c.close, 0);
    return sum / period;
  });
}

// 计算20日平均成交量
function calculateAvgVolume(candles: StockCandle[], index: number, period = 20): number {
  const start = Math.max(0, index - period + 1);
  const slice = candles.slice(start, index + 1);
  return slice.reduce((sum, c) => sum + c.volume, 0) / slice.length;
}

// 计算简化版信号（基于价格动量和成交量）
function calculateSignal(
  candles: StockCandle[],
  index: number,
  avgVolume: number
): SignalType {
  if (index < 5) return null; // 需要足够的历史数据

  const current = candles[index];
  const prev = candles[index - 1];
  const prev2 = candles[index - 2];

  const volumeRatio = current.volume / avgVolume;
  const priceChange = (current.close - prev.close) / prev.close;
  const prevPriceChange = (prev.close - prev2.close) / prev2.close;

  // 买入信号: 连续下跌后放量反弹
  if (
    prevPriceChange < -0.01 && // 前一天下跌
    priceChange > 0.015 && // 今天上涨超过1.5%
    volumeRatio > 1.3 // 成交量放大
  ) {
    return 'B';
  }

  // 卖出信号: 连续上涨后放量下跌
  if (
    prevPriceChange > 0.01 && // 前一天上涨
    priceChange < -0.015 && // 今天下跌超过1.5%
    volumeRatio > 1.3 // 成交量放大
  ) {
    return 'S';
  }

  // 异常成交量信号
  if (volumeRatio > 2.0) {
    return 'E';
  }

  return null;
}

// 计算连续趋势天数
function calculateTrendCount(candles: StockCandle[], index: number): number {
  if (index < 1) return 0;

  const current = candles[index];
  const isUp = current.close > current.open;
  let count = 1;

  for (let i = index - 1; i >= 0; i--) {
    const candle = candles[i];
    const candleIsUp = candle.close > candle.open;
    if (candleIsUp === isUp) {
      count++;
    } else {
      break;
    }
  }

  return isUp ? count : -count; // 正数表示连涨，负数表示连跌
}

// 处理数据
function processChartData(history: StockHistory): ChartDataPoint[] {
  const { candles } = history;

  return candles.map((candle, index) => {
    const isUp = candle.close >= candle.open;
    const avgVolume = calculateAvgVolume(candles, index);
    const volumeRatio = candle.volume / avgVolume;
    const signal = calculateSignal(candles, index, avgVolume);
    const trendCount = calculateTrendCount(candles, index);

    return {
      date: new Date(candle.timestamp).toLocaleDateString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
      }),
      timestamp: candle.timestamp,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
      volume: candle.volume,
      candleBody: isUp
        ? [candle.open, candle.close]
        : [candle.close, candle.open],
      wickHigh: candle.high,
      wickLow: candle.low,
      isUp,
      signal,
      signalY: signal === 'S' ? candle.high * 1.02 : candle.low * 0.98,
      trendCount,
      volumeRatio,
      isExceptional: volumeRatio > 2.0,
    };
  });
}

export function S1SignalChart({ history, showSignals = true, showMA = true }: S1SignalChartProps) {
  const { t } = useTranslation();
  const chartData = useMemo(() => processChartData(history), [history]);
  const ma5 = useMemo(() => calculateMA(history.candles, 5), [history.candles]);
  const ma20 = useMemo(() => calculateMA(history.candles, 20), [history.candles]);

  // 计算Y轴范围
  const [minPrice, maxPrice] = useMemo(() => {
    const prices = chartData.flatMap(d => [d.high, d.low]);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const padding = (max - min) * 0.1;
    return [min - padding, max + padding];
  }, [chartData]);

  // 获取信号点
  const signalPoints = useMemo(
    () => chartData.filter(d => d.signal),
    [chartData]
  );

  // 准备ECharts数据格式
  const echartsData = useMemo(() => {
    return chartData.map((d, idx) => ({
      value: [d.open, d.close, d.low, d.high],
      itemStyle: d.isExceptional ? {
        color: '#f97316',
        color0: '#f97316',
        borderColor: '#ea580c',
        borderColor0: '#ea580c',
      } : undefined,
      dataIndex: idx, // Store original index for tooltip
    }));
  }, [chartData]);

  const option = useMemo(() => {
    return {
      animation: false,
      grid: {
        top: 40,
        right: 30,
        left: 70,
        bottom: 50,
      },
      xAxis: {
        type: 'category',
        data: chartData.map(d => d.date),
        axisLine: { lineStyle: { color: '#cbd5e1' } },
        axisTick: { lineStyle: { color: '#cbd5e1' } },
        axisLabel: {
          fontSize: 11,
          interval: 'auto',
        },
        boundaryGap: true,
      },
      yAxis: {
        type: 'value',
        scale: true,
        min: minPrice,
        max: maxPrice,
        axisLabel: {
          formatter: (value: number) => `$${value.toFixed(0)}`,
          fontSize: 11,
        },
        axisLine: { lineStyle: { color: '#cbd5e1' } },
        splitLine: {
          lineStyle: {
            color: '#e2e8f0',
            type: 'dashed' as const,
          }
        },
      },
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: {
          type: 'cross' as const,
        },
        formatter: (params: any) => {
          if (!params || !params[0]) return '';
          const dataIndex = params[0].dataIndex;
          const data = chartData[dataIndex];
          if (!data) return '';

          const changePercent = ((data.close - data.open) / data.open * 100).toFixed(2);
          const isUp = data.close >= data.open;
          const ma5Value = ma5[dataIndex];
          const ma20Value = ma20[dataIndex];

          let html = `
            <div style="padding: 8px; font-size: 12px; min-width: 200px;">
              <div style="font-weight: 600; color: #0f172a; margin-bottom: 8px;">${data.date}</div>
              <div style="display: grid; grid-template-columns: auto 1fr; gap: 4px 16px;">
                <span style="color: #64748b;">开盘:</span>
                <span style="text-align: right;">$${data.open.toFixed(2)}</span>
                <span style="color: #64748b;">最高:</span>
                <span style="text-align: right;">$${data.high.toFixed(2)}</span>
                <span style="color: #64748b;">最低:</span>
                <span style="text-align: right;">$${data.low.toFixed(2)}</span>
                <span style="color: #64748b;">收盘:</span>
                <span style="text-align: right; font-weight: 600; color: ${isUp ? '#16a34a' : '#dc2626'};">
                  $${data.close.toFixed(2)}
                </span>
                <span style="color: #64748b;">涨跌:</span>
                <span style="text-align: right; color: ${isUp ? '#16a34a' : '#dc2626'};">
                  ${isUp ? '+' : ''}${changePercent}%
                </span>
                <span style="color: #64748b;">成交量:</span>
                <span style="text-align: right;">${(data.volume / 1e6).toFixed(2)}M</span>
          `;

          if (showMA && (ma5Value !== null || ma20Value !== null)) {
            html += `
                <span style="color: #64748b;">MA5:</span>
                <span style="text-align: right; color: #2563eb; font-weight: 600;">
                  ${ma5Value !== null ? `$${ma5Value.toFixed(2)}` : '-'}
                </span>
                <span style="color: #64748b;">MA20:</span>
                <span style="text-align: right; color: #f59e0b; font-weight: 600;">
                  ${ma20Value !== null ? `$${ma20Value.toFixed(2)}` : '-'}
                </span>
            `;
          }

          if (data.volumeRatio > 1.5) {
            html += `
                <span style="color: #64748b;">量比:</span>
                <span style="text-align: right; ${data.isExceptional ? 'color: #f97316; font-weight: 600;' : ''}">
                  ${data.volumeRatio.toFixed(2)}x
                </span>
            `;
          }

          if (data.signal) {
            const signalColor = data.signal === 'B' ? '#16a34a' :
                               data.signal === 'S' ? '#ec4899' : '#f97316';
            const signalText = data.signal === 'B' ? '买入' :
                              data.signal === 'S' ? '卖出' : '异常量';
            html += `
                <span style="color: #64748b;">信号:</span>
                <span style="text-align: right; font-weight: 600; color: ${signalColor};">
                  ${signalText}
                </span>
            `;
          }

          if (Math.abs(data.trendCount) >= 3) {
            const trendColor = data.trendCount > 0 ? '#16a34a' : '#dc2626';
            html += `
                <span style="color: #64748b;">趋势:</span>
                <span style="text-align: right; font-weight: 600; color: ${trendColor};">
                  连续${Math.abs(data.trendCount)}天${data.trendCount > 0 ? '上涨' : '下跌'}
                </span>
            `;
          }

          html += `
              </div>
            </div>
          `;

          return html;
        },
      },
      series: [
        {
          type: 'candlestick' as const,
          data: echartsData,
          itemStyle: {
            color: '#22c55e', // bullish (up) color
            color0: '#ef4444', // bearish (down) color
            borderColor: '#16a34a',
            borderColor0: '#dc2626',
          },
          markPoint: showSignals && signalPoints.length > 0 ? {
            symbol: 'pin',
            symbolSize: 40,
            data: signalPoints.map(point => {
              const pointIndex = chartData.findIndex(d => d.date === point.date);
              return {
                coord: [pointIndex, point.signal === 'S' ? point.high * 1.015 : point.low * 0.985],
                value: point.signal,
                itemStyle: {
                  color: point.signal === 'B' ? '#22c55e' :
                         point.signal === 'S' ? '#ec4899' : '#f97316',
                },
                label: {
                  show: true,
                  formatter: '{c}',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: 12,
                },
              };
            }),
          } : undefined,
        },
        ...(showMA ? [
          {
            type: 'line' as const,
            data: ma5,
            symbol: 'none',
            smooth: true,
            lineStyle: { width: 1.2, color: '#2563eb' },
            z: 3,
          },
          {
            type: 'line' as const,
            data: ma20,
            symbol: 'none',
            smooth: true,
            lineStyle: { width: 1.2, color: '#f59e0b' },
            z: 2,
          },
        ] : []),
      ],
    };
  }, [chartData, echartsData, ma5, ma20, minPrice, maxPrice, showMA, showSignals, signalPoints]);

  return (
    <div className="w-full h-96">
      <ReactECharts
        option={option}
        style={{ height: '100%', width: '100%' }}
        opts={{ renderer: 'canvas' }}
        notMerge={true}
      />

      {/* 图例说明 */}
      <div className="flex items-center justify-center gap-6 mt-2 text-xs text-slate-600">
        {showMA && (
          <>
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-0.5 bg-blue-600"></span>
              <span>MA5</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-0.5 bg-amber-500"></span>
              <span>MA20</span>
            </div>
          </>
        )}
        {showSignals && (
          <>
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 bg-green-500 rounded-sm"></span>
            <span>上涨</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 bg-red-500 rounded-sm"></span>
            <span>下跌</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 bg-orange-500 rounded-sm"></span>
            <span>异常成交量(E)</span>
          </div>
          <div className="group relative flex items-center gap-1 cursor-help">
            <span className="text-green-500 font-bold">B↑</span>
            <span>买入信号</span>
            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block w-80 p-3 bg-slate-800 text-white text-xs rounded-lg shadow-xl z-[60] pointer-events-none">
              <div className="relative">
                {t('stock.buySignalDescription')}
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 border-8 border-transparent border-t-slate-800"></div>
              </div>
            </div>
          </div>
          <div className="group relative flex items-center gap-1 cursor-help">
            <span className="text-pink-500 font-bold">S↓</span>
            <span>卖出信号</span>
            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block w-80 p-3 bg-slate-800 text-white text-xs rounded-lg shadow-xl z-[60] pointer-events-none">
              <div className="relative">
                {t('stock.sellSignalDescription')}
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 border-8 border-transparent border-t-slate-800"></div>
              </div>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
